import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resetPasswordWithToken } from '../api/auth';
import { extractApiError } from '../utils/apiError';
import Footer from '../components/Footer';
import FloatingLanguageSwitcher from '../components/FloatingLanguageSwitcher';

import { EyeIcon, EyeSlashIcon } from '../components/icons/EyeIcons';
import { Card, Button, LinkButton } from '../components/ui';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(t('auth.reset.errors.missingToken'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.reset.errors.passwordMismatch'));
      return;
    }
    if (password.length < 6) {
      setError(t('auth.reset.errors.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      const res = await resetPasswordWithToken(token, password);
      setSuccess(res.message);
    } catch (err: unknown) {
      setError(extractApiError(err, t('auth.reset.errors.default')));
    } finally {
      setLoading(false);
    }
  };

  // Local input pattern, mirroring the AuthInput look from LoginPage.
  const inputClass =
    'w-full rounded-[11px] border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] text-slate-900 placeholder-slate-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 dark:border-white/10 dark:bg-slate-900/80 dark:text-white dark:placeholder-slate-500';

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      <FloatingLanguageSwitcher />
      <main id="main-content" tabIndex={-1} className="flex flex-1 items-center justify-center px-4 outline-none">
        <div className="w-full max-w-md">
          <Card className="space-y-6 p-8">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <defs>
                  <linearGradient id="logoGrad" x1="4" y1="8" x2="44" y2="40" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#3B82F6"/>
                    <stop offset="100%" stopColor="#4F46E5"/>
                  </linearGradient>
                </defs>
                <path d="M13.5 38C7.701 38 3 33.299 3 27.5c0-4.58 2.942-8.468 7.038-9.87C10.98 11.583 16.046 7 22 7c6.904 0 12.5 5.596 12.5 12.5 0 .463-.025.92-.074 1.37C38.014 22.16 41 25.616 41 29.75 41 34.86 36.86 39 31.75 39H13.5z" fill="url(#logoGrad)"/>
                <path d="M13.5 38C7.701 38 3 33.299 3 27.5c0-4.58 2.942-8.468 7.038-9.87C10.98 11.583 16.046 7 22 7c6.904 0 12.5 5.596 12.5 12.5 0 .463-.025.92-.074 1.37C38.014 22.16 41 25.616 41 29.75 41 34.86 36.86 39 31.75 39H13.5z" fill="white" opacity="0.15" mask="url(#logoShine)"/>
                <mask id="logoShine"><rect x="0" y="0" width="48" height="24" fill="white"/></mask>
              </svg>
              <h1 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                {t('auth.reset.title')}
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t('auth.reset.subtitle')}
              </p>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
                {error}
              </div>
            )}

            {success ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-400">
                  {success}
                </div>
                <LinkButton to="/login" variant="primary" className="w-full">
                  {t('auth.reset.goToSignIn')}
                </LinkButton>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('auth.reset.newPasswordLabel')}
                  </label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${inputClass} pr-10`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                      aria-label={showPassword ? t('auth.reset.hidePassword') : t('auth.reset.showPassword')}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-new-password" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('auth.reset.confirmPasswordLabel')}
                  </label>
                  <input
                    id="confirm-new-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={inputClass}
                    placeholder="••••••••"
                  />
                </div>

                <Button type="submit" variant="primary" disabled={loading} className="w-full">
                  {loading ? t('auth.reset.submitting') : t('auth.reset.submit')}
                </Button>
              </form>
            )}

            <p className="text-center text-sm text-slate-500 dark:text-slate-400">
              <Link to="/login" className="font-medium text-blue-600 hover:underline dark:text-blue-400">
                {t('auth.reset.backToSignIn')}
              </Link>
            </p>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
